import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { formatPesos } from "@/lib/currency";
import { Globe, Wallet, LogOut, User as UserIcon, Shield, Menu, Award, BarChart3 } from "lucide-react";
import { useState } from "react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

export default function Navbar() {
    const { user, logout } = useAuth();
    const { t, lang, setLanguage } = useI18n();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const navLinks = [
        { to: "/mercados", label: t("nav.markets"), id: "nav-markets" },
        { to: "/en-vivo", label: t("nav.live"), id: "nav-live" },
        { to: "/rankings", label: t("nav.rankings"), id: "nav-rankings" },
        { to: "/creadores", label: t("nav.creators"), id: "nav-creators" },
    ];

    const linkClass = ({ isActive }) =>
        `text-sm font-medium tracking-tight transition-colors ${
            isActive ? "text-p2w-green" : "text-white/80 hover:text-p2w-green"
        }`;

    return (
        <header
            data-testid="navbar"
            className="sticky top-0 z-50 backdrop-blur-xl bg-[#0A0B12]/75 border-b border-p2w-line"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
                <div className="flex items-center gap-10">
                    <Link
                        to="/"
                        data-testid="logo-link"
                        className="font-sora font-extrabold text-2xl tracking-tight"
                    >
                        Play2<span className="text-p2w-green drop-shadow-[0_0_10px_rgba(159,232,13,0.6)]">Win</span>
                    </Link>
                    <nav className="hidden md:flex items-center gap-7">
                        {navLinks.map((l) => (
                            <NavLink key={l.to} to={l.to} className={linkClass} data-testid={l.id}>
                                {l.label}
                            </NavLink>
                        ))}
                    </nav>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        data-testid="lang-toggle"
                        onClick={() => setLanguage(lang === "es" ? "en" : "es")}
                        className="hidden sm:flex items-center gap-1.5 text-xs uppercase tracking-widest text-p2w-gray hover:text-white transition-colors px-3 py-2 rounded-lg hover:bg-p2w-elevated"
                    >
                        <Globe className="w-3.5 h-3.5" />
                        {lang === "es" ? "ES" : "EN"}
                    </button>

                    {user && user !== false && (
                        <>
                            <button
                                onClick={() => navigate("/wallet")}
                                aria-label="Tu saldo"
                                title="Tu saldo en Play2Win"
                                data-testid="wallet-badge"
                                className="group flex items-center gap-2 px-3 py-1.5 rounded-full bg-p2w-elevated border border-p2w-line hover:border-p2w-green/60 hover:shadow-[0_0_16px_rgba(159,232,13,0.25)] transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-p2w-green"
                            >
                                <Wallet className="w-4 h-4 text-p2w-green" />
                                <span className="text-sm font-sora font-bold text-p2w-green number-tabular" data-testid="wallet-badge-amount">
                                    {formatPesos(user.points_balance || 0)}
                                </span>
                            </button>
                            <Button
                                size="sm"
                                onClick={() => navigate("/wallet/recargar")}
                                data-testid="nav-recargar-btn"
                                className="hidden sm:inline-flex bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 hover:shadow-glow-green font-semibold"
                            >
                                Recargar
                            </Button>
                        </>
                    )}

                    {user && user !== false ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    data-testid="user-menu-trigger"
                                    title={`@${user.username}`}
                                    className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full bg-p2w-elevated border border-p2w-line hover:border-p2w-green/40 transition"
                                >
                                    <Avatar className="w-7 h-7 ring-1 ring-p2w-green/30">
                                        <AvatarImage src={user.avatar_url} alt={user.username} />
                                        <AvatarFallback className="bg-p2w-purple text-white text-xs">
                                            {(user.username || "?").slice(0, 2).toUpperCase()}
                                        </AvatarFallback>
                                    </Avatar>
                                    <span className="hidden md:inline text-xs font-semibold text-white">
                                        @{user.username}
                                    </span>
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="end"
                                className="bg-p2w-elevated border-p2w-line text-white min-w-[200px]"
                            >
                                <DropdownMenuLabel className="text-xs text-p2w-gray font-normal">
                                    @{user.username}
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator className="bg-p2w-line" />
                                <DropdownMenuItem
                                    data-testid="menu-profile"
                                    onClick={() => navigate(`/perfil/${user.username}`)}
                                    className="cursor-pointer focus:bg-p2w-line"
                                >
                                    <UserIcon className="w-4 h-4 mr-2" /> {t("nav.profile")}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    data-testid="menu-my-account"
                                    onClick={() => navigate("/mi-perfil")}
                                    className="cursor-pointer focus:bg-p2w-line"
                                >
                                    <BarChart3 className="w-4 h-4 mr-2" /> Mi cuenta
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    data-testid="menu-levels"
                                    onClick={() => navigate("/niveles")}
                                    className="cursor-pointer focus:bg-p2w-line"
                                >
                                    <Award className="w-4 h-4 mr-2" /> Niveles de la Liga
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    data-testid="menu-wallet"
                                    onClick={() => navigate("/wallet")}
                                    className="cursor-pointer focus:bg-p2w-line"
                                >
                                    <Wallet className="w-4 h-4 mr-2" /> {t("nav.wallet")}
                                </DropdownMenuItem>
                                {user.role === "admin" && (
                                    <DropdownMenuItem
                                        data-testid="menu-admin"
                                        onClick={() => navigate("/admin")}
                                        className="cursor-pointer focus:bg-p2w-line"
                                    >
                                        <Shield className="w-4 h-4 mr-2" /> {t("nav.admin")}
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator className="bg-p2w-line" />
                                <DropdownMenuItem
                                    data-testid="menu-logout"
                                    onClick={async () => {
                                        await logout();
                                        navigate("/");
                                    }}
                                    className="cursor-pointer focus:bg-p2w-line text-p2w-red"
                                >
                                    <LogOut className="w-4 h-4 mr-2" /> {t("nav.logout")}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : (
                        <>
                            <Button
                                variant="ghost"
                                data-testid="nav-login-btn"
                                onClick={() => navigate("/login")}
                                className="hidden sm:inline-flex text-white hover:bg-p2w-elevated hover:text-white"
                            >
                                {t("nav.login")}
                            </Button>
                            <Button
                                data-testid="nav-register-btn"
                                onClick={() => navigate("/registro")}
                                className="bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 hover:shadow-glow-green font-semibold"
                            >
                                {t("nav.register")}
                            </Button>
                        </>
                    )}

                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild>
                            <button
                                className="md:hidden p-2 rounded-lg hover:bg-p2w-elevated"
                                data-testid="mobile-menu-trigger"
                                aria-label="Menu"
                            >
                                <Menu className="w-5 h-5" />
                            </button>
                        </SheetTrigger>
                        <SheetContent side="right" className="bg-p2w-surface border-l border-p2w-line text-white w-[280px]">
                            <div className="mt-8 flex flex-col gap-2">
                                {navLinks.map((l) => (
                                    <NavLink
                                        key={l.to}
                                        to={l.to}
                                        onClick={() => setOpen(false)}
                                        className="px-3 py-3 rounded-lg text-base font-medium hover:bg-p2w-elevated"
                                    >
                                        {l.label}
                                    </NavLink>
                                ))}
                                <button
                                    onClick={() => setLanguage(lang === "es" ? "en" : "es")}
                                    className="mt-4 px-3 py-2 text-left text-sm text-p2w-gray"
                                >
                                    <Globe className="inline w-4 h-4 mr-2" />
                                    {lang === "es" ? "English" : "Español"}
                                </button>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
}
